import { useSuspenseQuery } from "@apollo/client";
import { Box, Stack, Typography } from "@mui/material";
import { pink } from "@mui/material/colors";
import { DashboardTile } from "components/Dashboard/DashboardTile";
import { useMemo } from "react";
import { BarChart } from "components/Dashboard/BarChart";
import { formatNumberWithAbbreviation } from "util/formatNumber";
import { DateTime } from "luxon";
import { gql } from "generated-graphql";
import { FacilityWasteView } from "generated-graphql/graphql";
import { ManifestType } from "./WasteStreamDetailsModal";

// Query for waste stream specific monthly shipments
const WASTE_STREAM_SHIPMENTS_BY_MONTH = gql(`
  query WasteStreamShipmentsByMonth($wasteStreamId: ID!, $filter: FacilityWasteShipmentsFilter!) {
    wasteStreamShipmentsByMonth(wasteStreamId: $wasteStreamId, filter: $filter) {
      wasteShipmentsByMonth {
        month
        vendorAmount
        eManifestAmount
      }
    }
  }
`);

interface WasteStreamShipmentsByMonthProps {
  wasteStreamId: string;
  startDate: string;
  endDate: string;
  manifestType: ManifestType;
}

export const WasteStreamShipmentsByMonth = ({
  wasteStreamId,
  startDate,
  endDate,
  manifestType,
}: WasteStreamShipmentsByMonthProps) => {
  const start = startDate ? DateTime.fromISO(startDate) : null;
  const end = endDate ? DateTime.fromISO(endDate) : null;

  // Get waste stream specific shipment data
  const { data } = useSuspenseQuery(WASTE_STREAM_SHIPMENTS_BY_MONTH, {
    variables: {
      wasteStreamId: wasteStreamId,
      filter: {
        startDate: start?.toJSDate(),
        endDate: end?.toJSDate(),
        view: "ALL" as FacilityWasteView,
        tags: [],
      },
    },
  });

  // Process the waste stream specific monthly data
  const chartData = useMemo(() => {
    if (!data?.wasteStreamShipmentsByMonth?.wasteShipmentsByMonth) {
      return [];
    }

    return data.wasteStreamShipmentsByMonth.wasteShipmentsByMonth.map(
      (monthData: any) => {
        const totalMonthlyAmount =
          manifestType === "vendor"
            ? monthData.vendorAmount
            : monthData.eManifestAmount;
        const date = DateTime.fromISO(monthData.month);
        return {
          month: date.isValid ? date.toFormat("MMM") : monthData.month, // Just 3-letter month for x-axis
          monthYear: date.isValid ? date.toFormat("MMM yyyy") : monthData.month, // Month + year for tooltip
          amount: totalMonthlyAmount,
        };
      }
    );
  }, [data, manifestType]);

  return (
    <Stack sx={{ width: "100%" }}>
      <DashboardTile.ContentArea>
        <Typography variant="body1">
          {`Waste stream shipments in lbs (${manifestType})`}
        </Typography>
        <Box sx={{ height: 200, width: "100%", color: "text.primary" }}>
          <BarChart
            dataset={
              chartData.length ? chartData : [{ amount: 0, label: "No data" }]
            }
            series={[
              {
                type: "bar",
                dataKey: "amount",
                color: pink[200],
                valueFormatter: (value) =>
                  `${formatNumberWithAbbreviation(value, "lbs")}`,
              },
            ]}
            xAxis={[
              {
                scaleType: "band",
                dataKey: "month",
                categoryGapRatio: 0.4,
              },
            ]}
            yAxis={[{ scaleType: "linear" }]}
            tooltipTitleProperty="monthYear"
            margin={{ top: 25, bottom: 25 }}
          />
        </Box>
      </DashboardTile.ContentArea>
    </Stack>
  );
};
